<script setup lang="ts">
import { TabsIndicator } from 'radix-vue'
import type { ITabsItems } from '~/ts/interfaces/Tabs'
import { useSendEvent } from '~/composables/useSendEvent'

interface Props {
    tabNames: ITabsItems[]
    big?: boolean
    footerTab?: boolean
    additionalClasses?: string | string[]
    statsComponentName?: string
}

const props = withDefaults(defineProps<Props>(), {
    tabNames: () => [],
    big: false,
    footerTab: false,
    additionalClasses: '',
    statsComponentName: 'tab',
})

const model = defineModel<string>({ required: true })

const { sendEvent } = useSendEvent()

watch(model, (val) => {
    sendEvent({
        action: 'click',
        label: '',
        click_text: val || '',
        click_path: '',
        click_element: 'tab',
        component_name: props.statsComponentName,
        offer: '',
    })
})
</script>

<template>
    <PlusBlogLpMoleculeTabs
        v-model="model"
        :big="big"
    >
        <div
            v-if="!props.tabNames.length"
            class="md:container"
        >
            <PlusBlogLpAtomSkeleton
                class="h-12 w-1/2"
                :class="[big ? 'rounded-2xl' : 'rounded-xl']"
            />
        </div>
        <div
            v-else
            class="hide-scrollbar max-w-full animate-lp-fade-in overflow-auto pl-4 md:pl-0"
            :class="[additionalClasses, !footerTab && 'plus-lp-container pl-4']"
        >
            <PlusBlogLpMoleculeTabsList
                class="relative"
                :class="big ? 'rounded-2xl lg:w-2/3' : 'rounded-xl'"
            >
                <TabsIndicator
                    class="lp-ease-in-out absolute left-[--radix-tabs-indicator-position] h-[calc(100%-8px)] w-[--radix-tabs-indicator-size] bg-plus-lp-dark-blue transition-all duration-400 hover:bg-green-500"
                    :class="big ? 'rounded-2xl' : 'rounded-xl'"
                />

                <PlusBlogLpMoleculeTabsTrigger
                    v-for="tab in props.tabNames"
                    :key="tab.id"
                    :value="tab.id"
                    :active-value="model"
                    :class="[
                        big ? 'rounded-2xl lg:flex-1' : 'rounded-xl px-4 py-2',
                        footerTab ? 'xl:w-[138px]' : 'w-auto',
                    ]"
                >
                    <span v-html="tab.name" />
                </PlusBlogLpMoleculeTabsTrigger>
            </PlusBlogLpMoleculeTabsList>
        </div>
        <slot />
    </PlusBlogLpMoleculeTabs>
</template>

<style lang="scss" scoped>
.hide-scrollbar {
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    scrollbar-width: none; /* Firefox */
}

.hide-scrollbar::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
}
</style>
